import { DonationProvider } from '@joindeed/calculate-fees';
import React from 'react';

type Props = {
  payPalModel: string;
  donationProvider: DonationProvider;
  countryCode: keyof typeof PPGFMessage;
};

const PPGFMessage = {
  US: (
    <div>
      Your donation is being made to PayPal Giving Fund, an IRS-registered 501(c)(3) charity,
      subject to its{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/us/webapps/mpp/givingfund/policies/donor-terms-of-service"
        rel="noreferrer"
      >
        terms
      </a>
      . The charity you recommend typically will receive the funds within 15-45 days of your
      original donation. This doesn&apos;t happen often but if PayPal Giving Fund cannot fund the
      charity you recommend, it will reassign the funds to a similar charity and whenever possible
      will consult with you on the reassignment. For more information visit its{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/us/webapps/mpp/givingfund/policies"
        rel="noreferrer"
      >
        Donation Delivery Policy
      </a>
      .
    </div>
  ),
  AU: (
    <div>
      You&apos;re donating to PayPal Giving Fund Australia (PPGF), a registered charity (ABN 65 105
      950 945) subject to its{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/au/webapps/mpp/givingfund/policies/donor-terms-of-service"
        rel="noreferrer"
      >
        terms
      </a>
      . PPGF typically grants funds to recommended charities within 45 days (90 days if the charity
      has not enrolled). In the rare event PPGF{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/au/webapps/mpp/givingfund/policies/donation-delivery"
        rel="noreferrer"
      >
        can&apos;t fund your chosen charity
      </a>
      , it will seek to consult you before reassigning the funds to a similar cause.
    </div>
  ),
  GB: (
    <div>
      You&apos;re donating to PayPal Giving Fund UK (PPGF), a registered charity (No. 1110538). PPGF
      typically grants funds to recommended charities within 45 days (90 days if the charity has not
      enrolled). In the rare event PPGF{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/uk/webapps/mpp/givingfund/policies#DonationDeliveryPolicy"
        rel="noreferrer"
      >
        can&apos;t fund your chosen charity
      </a>
      , it will seek to consult you before reassigning the funds to a similar cause.
    </div>
  ),
  CA: (
    <div>
      You&apos;re donating to PayPal Giving Fund Canada (PPGF), a charity registered with the Canada
      Revenue Agency (No.812867752RR0001), subject to its{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/ca/webapps/mpp/givingfund/policies/donor-terms-of-service"
        rel="noreferrer"
      >
        terms
      </a>
      . PPGF typically grants funds to recommended charities within 45 days (90 days if the charity
      has not enrolled). In the rare event PPGF{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/CA/webapps/mpp/givingfund/policies/donation-delivery-policy"
        rel="noreferrer"
      >
        can&apos;t fund your chosen charity
      </a>
      , it will seek to consult you before reassigning the funds to a similar cause.
    </div>
  ),
  IE: (
    <div>
      You&apos;re donating to PayPal Giving Fund Ireland (PPGF), a registered charity (No.
      20205692), subject to its{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/ie/webapps/mpp/givingfund/donortermsofservice"
        rel="noreferrer"
      >
        terms
      </a>
      . PPGF typically grants funds to recommended charities within 45 days (90 days if the charity
      has not enrolled). In the rare event PPGF{' '}
      <a
        target="_blank"
        href="https://www.paypal.com/ie/webapps/mpp/givingfund/donation-delivery-policy"
        rel="noreferrer"
      >
        can&apos;t fund your chosen charity
      </a>
      , it will seek to consult you before reassigning the funds to a similar cause.
    </div>
  ),
};
const DonationProviderMessage = ({ donationProvider, payPalModel, countryCode }: Props) => {
  const providerMessage: Record<string, JSX.Element | undefined> = {
    PayPal:
      payPalModel === 'DIRECT' ? (
        <div>Your donation will be sent directly to the nonprofit&apos;s PayPal account.</div>
      ) : (
        PPGFMessage[countryCode]
      ),
    GlobalGiving: (
      <div>
        By making a donation, you agree to GlobalGiving&apos;s Terms of Service & Privacy Policy.
      </div>
    ),
    Stripe: (
      <div>
        All of your card and payment data is securely stored by Stripe. Nothing is stored or saved
        on our servers.
      </div>
    ),
    NFG: (
      <div>
        Your donation is being made to a donor advised fund at Network for Good, Inc., a Delaware
        nonprofit corporation and a Section 501(c)(3) tax-exempt organization (EIN: 68-0480736).
        100% of your contribution is tax-deductible to the extent otherwise allowed by law. No goods
        or services have been provided to you in return for your donation. Network for Good has
        exclusive legal control over your donation.
        <br />
        <br />
        Your donation is subject to our{' '}
        <a
          href="https://www.networkforgood.com/about/charitable-solicitation-disclosures/"
          target="_blank"
          rel="noreferrer"
        >
          Charitable Solicitation Disclosures
        </a>{' '}
        (details on disbursement timing and how the donor advised fund works) and our{' '}
        <a href="https://www.networkforgood.com/about/privacy/" target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        . Network for Good grants donated funds to other charities. Network for Good charges a 5%
        fee that is deducted from each donation. This fee is used to pay banks, credit card
        companies and other administrative costs. Feel free to refer to{' '}
        <a href="www.networkforgood.com/support" target="_blank" rel="noreferrer">
          Network For Good FAQs
        </a>{' '}
        for doubts and questions.
      </div>
    ),
  };
  return (
    <div
      style={{
        color: 'rgba(130, 130, 130, 1)',
        fontSize: 12,
        marginBottom: 36,
        paddingLeft: 25,
        paddingRight: 25,
      }}
    >
      {providerMessage[donationProvider]}
    </div>
  );
};

export default DonationProviderMessage;
